import React from "react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  GlobalStyles,
} from "@mui/material";
import colorPalette from "./colorPallete";

declare module "@mui/material/styles" {
  interface Palette {
    white: Palette["primary"];
    black: Palette["primary"];
    tomato: Palette["primary"];
  }
  interface PaletteOptions {
    white?: PaletteOptions["primary"];
    black?: PaletteOptions["primary"];
    tomato?: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    dashed: true;
  }
  interface ButtonPropsColorOverrides {
    white: true;
    black: true;
    tomato: true;
  }
}

declare module "@mui/lab/LoadingButton" {
  interface LoadingButtonPropsColorOverrides {
    white: true;
    black: true;
    tomato: true;
  }
}

const MAIN_BACKGROUND_COLOR = colorPalette.grey[100];

const theme = createTheme({
  palette: {
    ...colorPalette,
    white: colorPalette.white,
    black: colorPalette.black,
    tomato: colorPalette.tomato,
  },
  typography: {
    fontFamily: [
      "Poppins",
      "Lato",
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    allVariants: {
      marginTop: 0,
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h1: { fontSize: "1.75rem" },
    h2: { fontSize: "1.5rem" },
    h3: { fontSize: "1.35rem" },
    h4: { fontSize: "1.25rem" },
    h5: { fontSize: "1.15rem" },
    h6: { fontSize: "1.075rem" },
    body1: { fontWeight: 500 },
    body2: { fontWeight: 500 },
  },
  components: {
    MuiTablePagination: {
      styleOverrides: {
        displayedRows: { margin: 0 },
        selectLabel: { margin: 0 },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "0 10px 30px 0 rgb(82 63 105 / 5%)",
          backgroundColor: colorPalette.white.main,
          color: colorPalette.grey[700],
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          textTransform: "none",
          borderRadius: "0.475rem",
          padding: "calc(0.55rem + 1px) calc(1.25rem + 1px)",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
      variants: [
        {
          props: { variant: "dashed" },
          style: {
            textTransform: "none",
            border: `2px dashed ${colorPalette.blue[500]}`,
          },
        },
        {
          props: { variant: "dashed", color: "secondary" },
          style: {
            border: `4px dashed ${colorPalette.red[500]}`,
          },
        },
        {
          props: { variant: "outlined", color: "white" },
          style: {
            border: "1px solid white",
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)",
            },
          },
        },
      ],
    },
  },
});

const ThemeWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={{ body: { backgroundColor: MAIN_BACKGROUND_COLOR } }}
        />
        {children}
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default ThemeWrapper;
